/**
 * RWBR — THE BOOTH: the researcher.
 *
 * NARRATION_MODEL §8.3: *"Facts are the variety engine. A line anchored to a
 * specific true fact cannot feel recycled, because race state never repeats."*
 *
 * This is the module that does most of the actual work of not sounding like a
 * loop. "He's led 35 of the last 40 turns" is unique to this race. No phrasebook
 * can compete with that, and it costs a query rather than a model call.
 *
 * THE ABSOLUTE RULE (D-003). Every fact returned here is READ FROM STATE the
 * engine produced. Nothing is derived by guessing, rounded into something
 * punchier, or inferred from a plausible pattern. If the state cannot support a
 * claim, the researcher returns fewer facts — never a softer version of one it
 * could not stand up.
 *
 * §8.3 caps this at three facts a beat. That cap is real: given six true things
 * the temptation is to say all six, and a booth that recites statistics is a
 * different and worse failure than one that repeats itself.
 */

import type { RaceResult } from '../race.ts';
import type { TurnResult } from '../turn.ts';
import type { Car, RaceState } from '../types.ts';
import { lapsOver, ordinal } from './register.ts';
import type { TrackCard } from '../turn.ts';
import { shopOf, type BoothContext } from './context.ts';
import { feudClause, readFeud } from './feudRead.ts';

export const CITE_FACTS = 'NARRATION_MODEL §8.3 — at most three verified facts per beat';

export const MAX_FACTS = 3;

/**
 * How many facts travel to the WRITER, as opposed to into the sentence.
 *
 * D-EZ-74, and it is the one place §8.3's cap is deliberately split in two.
 * The cap exists because `factClause` CONCATENATES — every fact handed to
 * `buildLine` ends up in the line, so three is already the edge of what a
 * person says in one breath and six would be a recitation.
 *
 * A model does not concatenate. It SELECTS. Handing it six true things and
 * letting it use two is how a broadcaster works, and withholding four of them
 * to protect a limit that only binds the other code path was costing the
 * commentary its best material for no reason. The deterministic line still
 * takes `MAX_FACTS`; the payload takes this.
 */
export const MODEL_FACTS = 6;

export type FactKind =
  | 'race-status'
  | 'race-field'
  | 'race-winner'
  | 'photo-finish'
  | 'turns-led'
  | 'position-gain'
  | 'position-loss'
  | 'head-to-head'
  | 'chips'
  | 'laps-clean'
  | 'cars-out'
  | 'lead-changes'
  | 'from-the-back'
  | 'consecutive-led'
  /**
   * WHO IS DONE FOR THE DAY, BY NAME.
   *
   * Howard, 16 Aug 2026, looking at an eighteen-car Big One: *"reading this
   * gives me a ton of data but I still have no idea what happened and who got
   * knocked out… this is the story and currently, from the booth, there's very
   * little."*
   *
   * He was right, and the gap was structural rather than a matter of wording.
   * `cars-out` is `t.collected` — everyone CAUGHT UP in it, which includes the
   * man who got a fender rubbed and drove on. `t.retiredThisTurn` is the men
   * whose race actually ENDED, and nothing in this module had ever read it. So
   * the booth could say "18 cars caught up in it" and was structurally unable
   * to say that eight of them were finished, or to name a single one.
   *
   * A count is data. A name is the story.
   */
  | 'cars-retired'
  /**
   * A driver hurt in it. EZRA's injury tables produce a real season
   * consequence — a serious one costs him races — and the booth was silent on
   * every one of them.
   */
  | 'injury'
  /**
   * D-EZ-74 — THE FACTS THE ENGINE ALREADY HAD AND NOBODY ASKED FOR.
   *
   * Howard, 21 Aug 2026: *"is there any way to make the in race commentary
   * MORE. More conversation, more descriptive, more colorful, more
   * informative?"*
   *
   * The first eleven kinds above read ONE object: the running order, plus the
   * retirements and injuries hanging off the turn. Everything below was already
   * on the same `TurnResult` and was simply never queried — the pit turn's own
   * result, the laps-down flag, the shop that owns the car, the number painted
   * on its door, the men running on fumes. A booth that cannot say "the 71 car"
   * or "he is a lap down" is not short of vocabulary. It is short of a SELECT.
   */
  /** These two, and what is actually between them. See `feudRead.ts`. */
  | 'feud'
  /** What happened on pit road — a stop won, a stop lost, a penalty served. */
  | 'pit-stop'
  /** A penalty off Appendix B1, in the words of its consequence. */
  | 'pit-penalty'
  /** Off the lead lap, which is the difference between a bad day and a lost one. */
  | 'laps-down'
  /** The number on the door. Broadcast shorthand, and free. */
  | 'car-number'
  /** The shop that runs the car — only when the caller handed us the roster. */
  | 'shop'
  /** Running on fumes, p.xv — a man who skipped the last stop. */
  | 'low-fuel'
  /** Fuming, p.x — he did not start a feud, he is just furious. */
  | 'fuming'
  /** How many are left. The shape of a race of attrition. */
  | 'still-running'
  /** What the injury actually costs him, in races. */
  | 'injury-cost'
  /** The crew, not the driver — a hurt crew is a slow stop for the rest of the day. */
  | 'crew'
  /**
   * D-EZ-102 — what the LEAGUE knew coming in (`BoothContext.season`): the
   * defending winner, the points picture, a milestone the win reached.
   */
  | 'season'
  /** D-EZ-111 — a storyline the booth opened before the green, and how it stands. */
  | 'storyline'
  /** D-EZ-111 — two drivers' history from the rivalry book, up to the previous race. */
  | 'rivalry'
  /** D-EZ-111 — the analyst's pre-race pick and how it is going. Opinion, never a result. */
  | 'take'
  /** D-EZ-111 — a fact about the booth itself (the analyst's own past). Fiction about the cast only. */
  | 'booth'
  /** D-EZ-111 — a fictional driver's words (people layer), after the event they are about. */
  | 'quote'
  /** D-EZ-111 — league history up to the previous race (track, last week, streaks). */
  | 'history';

export interface Fact {
  kind: FactKind;
  /** The finished clause, ready to drop into a line. */
  text: string;
  /** Whom it is about. Empty for facts about the race itself. */
  driverIds: string[];
  /** The number behind it, so the editor can check a superlative against it. */
  value: number;
}

const nameOf = (state: RaceState, driverId: string): string =>
  [...state.grid, ...state.retired].find((c) => c.card.driverId === driverId)?.card.name ?? driverId;

const carOf = (state: RaceState, driverId: string): Car | undefined =>
  [...state.grid, ...state.retired].find((c) => c.card.driverId === driverId);

/**
 * Everything true about this turn that is worth saying, best first.
 *
 * Ordered by how much a listener would care, not by how easy it was to
 * compute — the caller takes the first `MAX_FACTS` and the rest are dropped.
 */
export function factsFor(
  race: RaceResult,
  t: TurnResult,
  driverIds: readonly string[],
  track: Pick<TrackCard, 'lengthMiles'>,
  /**
   * D-EZ-74 — what the booth is allowed to know beyond the race. Optional, and
   * every fact built from it is skipped when it is absent, so an exhibition
   * race gets exactly the researcher it had before this existed.
   */
  ctx: BoothContext = {},
): Fact[] {
  const facts: Fact[] = [];
  const state = t.state;
  const prev = race.turns[t.turn - 2]?.state ?? null;

  // ---- about the race itself ---------------------------------------------
  /**
   * THE MEN WHOSE RACE ENDED, NAMED — and placed FIRST, above the count.
   *
   * `factsFor` returns best-first and the caller takes the top three, so
   * position in this list is the editorial judgement. "Eight cars are done"
   * outranks "eighteen were involved" because the first is the consequence and
   * the second is the scale of it.
   *
   * Named up to three deep and then counted, which is how a person actually
   * says it. Eighteen names is a list, not a sentence.
   */
  if (t.retiredThisTurn.length > 0) {
    const names = t.retiredThisTurn.map((id) => nameOf(state, id));
    const shown = names.slice(0, 3);
    const rest = names.length - shown.length;
    // "A, B and C" when that is the lot; "A, B, C and 12 more" when it is not.
    // Building the list with an "and" and then appending "and 12 more" gives
    // "Bo Barkley and 12 more" hanging off "Earl Ledbetter and" — two ands in
    // one clause, which is how you can tell a sentence was assembled.
    const who =
      rest > 0 || shown.length === 1
        ? shown.join(', ')
        : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
    facts.push({
      kind: 'cars-retired',
      text:
        rest > 0
          ? `${who} and ${rest} more are done for the day`
          : names.length === 1
            ? `${who} is done for the day`
            : `${who} are done for the day`,
      driverIds: [...t.retiredThisTurn],
      value: t.retiredThisTurn.length,
    });
  }

  /**
   * THE HURT. Only the ones that carry a consequence — `injuries` includes the
   * "no injury" reads, and a booth that announces every driver who climbed out
   * fine is a booth nobody listens to.
   */
  const hurt = t.injuries.filter((i) => i.severity !== 'none');
  if (hurt.length > 0) {
    const worst =
      hurt.find((i) => i.severity === 'serious') ??
      hurt.find((i) => i.severity === 'moderate') ??
      hurt[0];
    const who = nameOf(state, worst.driverId);
    const others = hurt.length - 1;
    facts.push({
      kind: 'injury',
      text:
        worst.severity === 'serious'
          ? others > 0
            ? `${who} is hurt badly enough to be out, and ${others} more are banged up`
            : `${who} is hurt badly enough to be out`
          : others > 0
            ? `${who} is shaken up, and so are ${others} more`
            : `${who} is shaken up`,
      driverIds: hurt.map((i) => i.driverId),
      value: hurt.length,
    });
  }

  /**
   * The SCALE of it, under the consequence. A count outranks nothing except
   * silence: "21 cars caught up in it" is the shape of the wreck, and the men
   * whose day it ended are the point of it.
   */
  if (t.collected.length >= 3) {
    facts.push({
      kind: 'cars-out',
      text: `${t.collected.length} cars caught up in it`,
      driverIds: [],
      value: t.collected.length,
    });
  }

  /**
   * ---- PIT ROAD ----------------------------------------------------------
   *
   * D-EZ-74. `t.pit` is a whole `PitTurnResult` — every fast-pit roll, every
   * Appendix B1 violation with the penalty it drew, whether the lead changed
   * in the pits — and the researcher had never opened it. On a pit turn the
   * booth was reduced to whatever the running order happened to look like
   * afterwards, which is why Ned Coyle, whose entire beat is pit road, had
   * nothing to report from pit road.
   */
  if (t.pit) {
    const won = t.pit.fastPit.rolls.filter((r) => r.earned);
    if (won.length > 0) {
      const names = won.slice(0, 2).map((r) => nameOf(state, r.driverId));
      const rest = won.length - names.length;
      facts.push({
        kind: 'pit-stop',
        text:
          rest > 0
            ? `${names.join(' and ')} got the stop, and ${rest} more`
            : names.length === 2
              ? `${names[0]} and ${names[1]} both got the stop`
              : `${names[0]} got the stop`,
        driverIds: won.map((r) => r.driverId),
        value: won.length,
      });
    }

    /*
      A PENALTY IS A CONSEQUENCE, NOT A CODE. `PitViolationResult.penalty` is a
      kebab-case kind off Appendix B1 and `description` is the chart's own
      wording, which cites and counts. Neither is a sentence a person says out
      loud, so this translates the kind and leaves the arithmetic on the ledger
      where the receipts already show it.
    */
    /*
      ⚠ THE WHOLE PREDICATE, NOT AN ADJECTIVE WITH `is` GLUED IN FRONT.

      The first version stored bare phrases and wrote `${who} is ${said}`, which
      gives "Elmo Rountree is sent to the tail of the field" — fine — and also
      "Elmo Rountree is has to come back down for a second stop", which is not
      English. Found by printing a whole race and reading it, which is the only
      way any of this ever gets found. Each entry is now a finished predicate.
    */
    const PENALTY_SAID: Record<string, string> = {
      'tail-of-field': 'is sent to the tail of the field',
      'pass-through-or-tail': 'has a pass-through coming',
      'lap-penalty': 'loses a lap for it',
      'second-stop': 'has to come back down for a second stop',
      'stop-and-go': 'owes a stop-and-go',
      discretion: 'is in the official\u2019s hands',
      'feud-roll': 'is going to hear about it',
    };
    for (const v of t.pit.violations) {
      const kind = v.chart?.roll.penalty;
      const said = kind ? PENALTY_SAID[kind] : undefined;
      if (!said) continue;
      facts.push({
        kind: 'pit-penalty',
        text: `${nameOf(state, v.driverId)} ${said}`,
        driverIds: [v.driverId],
        value: v.chipsForfeited,
      });
      break; // One penalty is a story. Four is a list.
    }

    if (t.pit.leadChanged) {
      const leader = t.state.grid[0];
      facts.push({
        kind: 'pit-stop',
        text: leader ? `${leader.card.name} came off pit road in front` : `the lead changed on pit road`,
        driverIds: leader ? [leader.card.driverId] : [],
        value: 1,
      });
    }

    /*
      A HURT CREW IS A SLOW STOP FOR THE REST OF THE AFTERNOON. It is carried on
      the pit result precisely because it persists, and it is the kind of thing
      a pit reporter is standing right next to.
    */
    if (t.pit.crewInjured.length > 0) {
      const who = nameOf(state, t.pit.crewInjured[0]);
      facts.push({
        kind: 'crew',
        text:
          t.pit.crewInjured.length === 1
            ? `${who} is a man short over the wall`
            : `${who} is one of ${t.pit.crewInjured.length} crews hurt today`,
        driverIds: [...t.pit.crewInjured],
        value: t.pit.crewInjured.length,
      });
    }
  }

  /**
   * ---- STANDING STATE IS ONLY NEWS WHEN IT MOVES -------------------------
   *
   * ⚠ THE FIRST VERSION OF THIS BLOCK TOOK THE BOOTH FROM 20 BEATS TO 3.
   *
   * Measured on a 22-turn road course the moment these three facts existed.
   * They are STANDING state: how many cars are left, how many are a lap down,
   * who is on fumes. All three are true on almost every turn, and all three
   * render as nearly the same sentence on almost every turn — so the editor's
   * five-gram ban (§8.7) refused line after line, the engine fallback shared
   * the same clauses and was refused too, and a race that should have had
   * twenty beats finished with three.
   *
   * The anti-repetition device eating the thing it exists to serve, again, and
   * this time WE fed it. The fix is not to loosen the gate. It is that
   *
   *     A NUMBER THAT HAS NOT CHANGED SINCE LAST TURN IS NOT NEWS,
   *
   * which is also simply truer: a broadcast says "we are down to 31 cars" when
   * a car goes, not every ten seconds for an hour. So each of these fires only
   * on the turn its value MOVED, which makes it rare, which makes it a story.
   */
  {
    const started = race.turns[0]?.state.grid.length ?? 0;
    const running = state.grid.filter((c) => c.status === 'running').length;
    const ranBefore = prev ? prev.grid.filter((c) => c.status === 'running').length : running;
    if (started > 0 && running > 0 && running < ranBefore && running <= Math.floor(started * 0.85)) {
      facts.push({
        kind: 'still-running',
        text: `${running} cars still running of the ${started} that took the green`,
        driverIds: [],
        value: running,
      });
    }
  }

  {
    const down = state.grid.filter((c) => c.lapsDown).length;
    const downBefore = prev ? prev.grid.filter((c) => c.lapsDown).length : 0;
    if (down > downBefore) {
      const fresh = down - downBefore;
      facts.push({
        kind: 'laps-down',
        text:
          fresh === 1
            ? down === 1
              ? `that puts a car a lap down`
              : `another one goes a lap down, ${down} of them now`
            : `${fresh} more go a lap down`,
        driverIds: [],
        value: down,
      });
    }
  }

  /** Running on fumes (p.xv) — reported the turn a man commits to it. */
  {
    const before = prev?.lowFuel ?? [];
    const fresh = state.lowFuel.filter((id) => !before.includes(id));
    if (fresh.length > 0) {
      const who = nameOf(state, fresh[0]);
      facts.push({
        kind: 'low-fuel',
        text:
          fresh.length === 1
            ? `${who} is going to try to make it on what he has`
            : `${who} is one of ${fresh.length} gambling on fuel`,
        driverIds: [...fresh],
        value: state.lowFuel.length,
      });
    }
  }

  const changesSoFar = race.leadChanges.filter((c) => c.turn <= t.turn).length;
  if (t.leadChangedTo !== null && changesSoFar >= 3) {
    facts.push({
      kind: 'lead-changes',
      text: `that's ${changesSoFar} lead changes`,
      driverIds: [],
      value: changesSoFar,
    });
  }

  // ---- about the drivers involved ----------------------------------------
  for (const id of driverIds) {
    const car = carOf(state, id);
    if (!car) continue;

    if (car.consecutiveTurnsLed >= 5) {
      const laps = lapsOver(track, car.consecutiveTurnsLed);
      facts.push({
        kind: 'consecutive-led',
        text:
          laps === null
            ? `${car.card.name} has led the last ${car.consecutiveTurnsLed} turns`
            : `${car.card.name} has led the last ${laps} laps`,
        driverIds: [id],
        value: car.consecutiveTurnsLed,
      });
    } else if (car.turnsLed >= 5) {
      facts.push({
        kind: 'turns-led',
        text: `${car.card.name} has led ${car.turnsLed} turns today`,
        driverIds: [id],
        value: car.turnsLed,
      });
    }

    // A RETIRED CAR HAS NOT MOVED ANYWHERE.
    //
    // `carOf` searches `retired` as well as `grid`, and a retired car keeps the
    // position it held. Diffing that against last turn produced sentences like
    // "Wiley Ingle a spot to the good" about a man whose race had just ended in
    // the wreck being described — measured on seed 555, turn 3. Every fact in
    // this module is supposed to be READ FROM STATE; this one was reading a
    // number that had stopped meaning what it says.
    const running = car.status === 'running';

    if (prev && running) {
      const before = prev.grid.find((c) => c.card.driverId === id)?.position;
      if (before !== undefined && before !== car.position) {
        const moved = before - car.position;
        const spots = Math.abs(moved) === 1 ? 'a spot' : `${Math.abs(moved)} spots`;
        // Attributed when two men are involved. "4 spots gained, 4 spots lost"
        // is two facts about two different people that reads as one confused
        // fact about one — the name is what makes it a sentence.
        const who = `${car.card.name} `;
        facts.push({
          kind: moved > 0 ? 'position-gain' : 'position-loss',
          text: moved > 0 ? `${who}gains ${spots}` : `${who}loses ${spots}`,
          driverIds: [id],
          value: Math.abs(moved),
        });
      }
    }

    // Where he started against where he is now — only when it is a real story.
    const gridIndex = race.startingOrder?.indexOf(id) ?? -1;
    const start = gridIndex >= 0 ? gridIndex + 1 : undefined;
    if (running && start !== undefined && start - car.position >= 10) {
      facts.push({
        kind: 'from-the-back',
        text: `${car.card.name} started ${ordinal(start)}`,
        driverIds: [id],
        value: start - car.position,
      });
    }

    if (running && car.chips >= 2) {
      facts.push({
        kind: 'chips',
        text: `${car.card.name} holds ${car.chips} performance chips`,
        driverIds: [id],
        value: car.chips,
      });
    }

    /**
     * THE NUMBER ON THE DOOR — D-EZ-74.
     *
     * `car.card.carNumber` has been one dereference away this whole time. It
     * is how a broadcast actually refers to a car, it costs nothing, and its
     * absence is a large part of why the booth read like a results service.
     * Placed LAST among this man's facts on purpose: it is colour, and colour
     * does not outrank a wreck.
     */
    if (car.card.carNumber) {
      facts.push({
        kind: 'car-number',
        text: `${car.card.name} drives the ${car.card.carNumber} car`,
        driverIds: [id],
        value: Number(car.card.carNumber) || 0,
      });
    }

    /** The shop, when the caller handed us the roster to join against. */
    const shop = shopOf(ctx, car.card.teamId);
    if (shop) {
      facts.push({
        kind: 'shop',
        text: `${car.card.name} runs for ${shop}`,
        driverIds: [id],
        value: 0,
      });
    }

    /**
     * This man specifically, off the lead lap — but only when the field-wide
     * fact above is not already reporting the same news. Both fired on the
     * same turn and the beat carried "that puts a car a lap down" and "he is a
     * lap down", which is one piece of information wearing two hats.
     */
    if (running && car.lapsDown && !facts.some((f) => f.kind === 'laps-down')) {
      facts.push({
        kind: 'laps-down',
        text: `${car.card.name} is a lap down`,
        driverIds: [id],
        value: 1,
      });
    }

    /**
     * FUMING (p.x) is not a feud. He has not been put on a list with anybody;
     * he is simply furious and may take it out on whoever is nearest in his own
     * group. That is a different and more dangerous sentence than a rivalry,
     * and the booth had no way to say it.
     */
    if (running && state.fuming.includes(id)) {
      facts.push({
        kind: 'fuming',
        text: `${car.card.name} is fuming`,
        driverIds: [id],
        value: 1,
      });
    }

    /**
     * WHAT THE INJURY COSTS. `InjuryResult` carries `racesMissed` — a number,
     * or the word `season` — and the researcher was reading only `severity`.
     * "He is hurt" is a moment. "He is out for the year" is the season turning
     * over, and it was on the same object.
     */
    const hisInjury = t.injuries.find((i) => i.driverId === id && i.severity !== 'none');
    if (hisInjury && hisInjury.racesMissed !== null) {
      facts.push({
        kind: 'injury-cost',
        text:
          hisInjury.racesMissed === 'season'
            ? `${car.card.name} misses the rest of the season`
            : hisInjury.racesMissed === 1
              ? `${car.card.name} misses next week`
              : `${car.card.name} misses the next ${hisInjury.racesMissed} races`,
        driverIds: [id],
        value: hisInjury.racesMissed === 'season' ? 99 : hisInjury.racesMissed,
      });
    }
  }

  // ---- these two ---------------------------------------------------------
  if (driverIds.length >= 2) {
    const [a, b] = driverIds;

    /**
     * THE FEUD, WITH SOMETHING IN IT — D-EZ-74.
     *
     * Placed ABOVE the head-to-head count because a grudge with a date on it
     * outranks a tally. `readFeud` does the work and `feudClause` decides what
     * is worth saying; both refuse rather than soften, so a pair with nothing
     * behind them produces no fact and the line falls through to the phrasing
     * it always had.
     */
    if (
      t.state.feuds.some((f) => (f.a === a && f.b === b) || (f.a === b && f.b === a))
    ) {
      const read = readFeud(race, a, b, t.turn, ctx);
      const clause = feudClause(read);
      if (clause) {
        facts.push({
          kind: 'feud',
          text: clause,
          driverIds: [a, b],
          value: read.racesThisSeason ?? read.metToday,
        });
      }
    }

    const met = race.events.filter(
      (e) => e.turn < t.turn && e.actorIds.includes(a) && e.actorIds.includes(b),
    ).length;
    if (met > 0) {
      facts.push({
        kind: 'head-to-head',
        text: `${nameOf(state, a)} and ${nameOf(state, b)} have shared ${met} earlier race event${met === 1 ? '' : 's'}`,
        driverIds: [a, b],
        value: met,
      });
    }
  }

  return facts;
}

/**
 * The `MAX_FACTS` best, in order, with duplicates removed.
 *
 * Two drivers who each moved two places produced "2 spots gained, 2 spots
 * gained" — both true, and together meaningless. Deduplicated on the rendered
 * TEXT rather than the kind, since it is the repeated words a listener hears.
 */
/**
 * ⚠ IDENTITY IS NOT NEWS EITHER, AND IT NEVER STOPS BEING TRUE.
 *
 * The number on the door and the name of the shop are true on every turn of
 * every race the man starts. Fed to `factClause`, which CONCATENATES, they
 * would appear in every line he is in — "the 71 car, he runs for Grain Racing"
 * over and over — and then the five-gram gate would refuse the lot, exactly as
 * the standing facts did before they were taught to fire only on a change.
 *
 * But they are genuinely useful to a writer that SELECTS: "the 71 car of Lonnie
 * Ackerd" is how a broadcast says it once and then drops it. So these are
 * withheld from the deterministic line and travel only in `moreFacts`, where a
 * model may use one and ignore it for the next twenty beats.
 *
 * That is the whole shape of D-EZ-74's fact split: the concatenating writer
 * gets what must be said, the selecting writer gets what could be said.
 */
export const COLOUR_KINDS: readonly FactKind[] = ['car-number', 'shop'];

const isColour = (f: Fact): boolean => COLOUR_KINDS.includes(f.kind);

/**
 * The kinds that ARE a given angle, so the fact the beat is about is not
 * ranked away by a wreck it is not about.
 *
 * D-EZ-74. `factsFor` returns best-first by how much a listener would care,
 * which is the right global order and the wrong one for a beat that has already
 * chosen its subject. A rivalry beat took the top three facts of the turn, and
 * on a busy turn the feud — the entire reason the angle was picked — was fourth
 * and got dropped. So the booth said "these two are not done with each other"
 * on a beat that was holding the sentence "this has been running since Bristol"
 * and threw it away.
 */
const ANGLE_KINDS: Partial<Record<string, readonly FactKind[]>> = {
  rivalry: ['feud', 'head-to-head', 'fuming'],
  consequence: ['pit-penalty', 'pit-stop', 'laps-down', 'injury-cost', 'cars-retired'],
  season: ['from-the-back', 'turns-led', 'consecutive-led', 'shop'],
  defence: ['consecutive-led', 'turns-led', 'chips'],
  stretch: ['still-running', 'laps-down', 'low-fuel', 'lead-changes'],
};

/**
 * `topFacts`, with the ones that ARE this angle pulled to the front first.
 * Falls straight through to `topFacts` for an angle with no pinned kinds.
 */
export const topFactsFor = (facts: readonly Fact[], angle: string): Fact[] => {
  const want = ANGLE_KINDS[angle];
  if (!want) return topFacts(facts);
  const usable = facts.filter((f) => !isColour(f));
  const pinned = usable.filter((f) => want.includes(f.kind));
  const rest = usable.filter((f) => !want.includes(f.kind));
  return bestFacts([...pinned, ...rest], MAX_FACTS);
};

/**
 * The best `n`, in order, with duplicates removed. `topFacts` is this at
 * `MAX_FACTS` and is what the deterministic line uses; the payload asks for
 * `MODEL_FACTS`.
 */
export const bestFacts = (facts: readonly Fact[], n: number): Fact[] => {
  const seen = new Set<string>();
  const out: Fact[] = [];
  for (const f of facts) {
    if (seen.has(f.text)) continue;
    seen.add(f.text);
    out.push(f);
    if (out.length >= n) break;
  }
  return out;
};

export const topFacts = (facts: readonly Fact[]): Fact[] =>
  bestFacts(facts.filter((f) => !isColour(f)), MAX_FACTS);

/**
 * The set that travels to a writer that SELECTS — colour last, and only ever
 * after everything that actually happened.
 *
 * ⚠ COLOUR WAS CROWDING OUT THE RACE. `bestFacts(allFacts, 6)` took the first
 * six in researcher order, and the car number and the shop are emitted inside
 * the per-driver loop — so on a two-driver beat, four of the six slots went to
 * "the 41 car", "he runs for Salvage Racing", "the 60 car", "he runs for
 * Whitaker Motorsports". Found by printing a race with the exchange bank
 * switched on and reading three consecutive beats that all opened *"The 14 car,
 * and that is not nothing."*
 *
 * Identity is always true and therefore never urgent. It goes at the back, and
 * a writer reaches it only when the ledger has run out of things that happened.
 */
export const forTheModel = (facts: readonly Fact[], n: number): Fact[] =>
  bestFacts([...facts.filter((f) => !isColour(f)), ...facts.filter(isColour)], n);

/**
 * Facts about a quiet stretch — how long it ran, and how clean.
 *
 * Deliberately thin. §8.6 wants ONE line for three uneventful turns, and the
 * fastest way to make a compression line feel padded is to decorate it.
 */
export function quietFacts(
  race: RaceResult,
  from: number,
  to: number,
  track: Pick<TrackCard, 'lengthMiles'>,
): Fact[] {
  const turns = to - from + 1;
  const laps = lapsOver(track, turns);
  const out: Fact[] = [
    {
      kind: 'laps-clean',
      text: laps === null ? `${turns} turns` : `${laps} laps`,
      driverIds: [],
      value: laps ?? turns,
    },
  ];

  const leader = race.turns[to - 1]?.state.grid[0];
  if (leader) {
    const held = race.turns
      .slice(from - 1, to)
      .every((t) => t.state.grid[0]?.card.driverId === leader.card.driverId);
    if (held) {
      out.push({
        kind: 'turns-led',
        text: `${nameOf(race.turns[to - 1].state, leader.card.driverId)} still out front`,
        driverIds: [leader.card.driverId],
        value: turns,
      });
    }
  }
  return out;
}

/**
 * A superlative is only permitted where it is literally true against the
 * ledger (§8.5). This is the check the editor runs, exposed here because the
 * researcher owns the counters.
 */
export function isBiggestIncidentSoFar(race: RaceResult, turn: number): boolean {
  const here = race.turns[turn - 1]?.collected.length ?? 0;
  if (here === 0) return false;
  return race.turns.slice(0, turn - 1).every((t) => t.collected.length < here);
}
